import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LIVE_CADENCE_LABELS, VER_STAGES, hasRoleAtLeast } from '@raro/domain';
import { Glyph, type GlyphName, AskBar, Breadcrumbs, Button, Card, EmptyState, Index, IndexItem, Label, Notice, Serif, Stepper, Tag, Select } from '@raro/ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { AddSourceDrawer } from '../components/AddSourceDrawer';
import { ContextBase } from '../components/ContextBase';
import { EvidenceCard } from '../components/EvidenceCard';
import { ContradictionsSection, HypothesesSection, ObservationsSection, QuestionsSection, SignalsSection } from '../components/InvestigationLists';
import { SourceList } from '../components/SourceList';
import { ClientRoomCuration } from '../components/ClientRoomCuration';
import { MachineSection } from '../components/Suggestions';
import { InterviewProfiles } from '../components/InterviewProfiles';
import { SessionsSection } from '../components/SessionsSection';
import { SolutionSection } from '../components/SolutionSection';
import { DecisionsSection, EffectsSection, InitiativesSection, LearningsSection } from '../components/VerSections';
import { ImagePicker } from '../components/ImagePicker';
import { PecasBoard } from '../components/PecasBoard';
import { WallCanvas } from '../components/WallCanvas';
import { api } from '../lib/api';
import { useBrands } from '../lib/brands';
import { useAuth } from '../lib/auth';
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS, daysLeft, sourceIsPending, stageLabel } from '../lib/format';
import type { Cycle, Evidence, ProjectDetail, ProjectSummary, Source, Suggestion } from '../lib/types';

type Section = 'sala' | 'machine' | 'parede' | 'sessoes' | 'entrevistas' | 'fontes' | 'evidencias' | 'observacoes' | 'sinais' | 'perguntas' | 'contradicoes' | 'hipoteses' | 'solucao' | 'iniciativas' | 'pecas' | 'decisoes' | 'efeitos' | 'aprendizados';
/** Ícone-marca de cada seção, no topo do conteúdo (o índice ao lado fica só com texto). */
const SECTION_GLYPH: Partial<Record<Section, GlyphName>> = {
  machine: 'machine', parede: 'parede', sessoes: 'sessoes', fontes: 'fontes', evidencias: 'evidencias', observacoes: 'observacoes', sinais: 'sinais',
  perguntas: 'perguntas', contradicoes: 'contradicoes', hipoteses: 'hipoteses', solucao: 'solucoes', iniciativas: 'iniciativas', pecas: 'pecas',
  decisoes: 'decisoes', efeitos: 'efeito', aprendizados: 'aprendizado', sala: 'sala',
};

const SECTIONS: { key: Section; label: string; sprint?: string; count?: (s: ProjectSummary) => number }[] = [
  { key: 'machine', label: 'Machine' },
  { key: 'parede', label: 'Parede', count: (s) => s.wallItems },
  { key: 'sessoes', label: 'Sessões', count: (s) => s.sessions ?? 0 },
  { key: 'entrevistas', label: 'Entrevistas' },
  { key: 'fontes', label: 'Fontes', count: (s) => s.sources },
  { key: 'evidencias', label: 'Evidências', count: (s) => s.evidence },
  { key: 'observacoes', label: 'Observações', count: (s) => s.observations },
  { key: 'sinais', label: 'Sinais', count: (s) => s.signals },
  { key: 'perguntas', label: 'Perguntas', count: (s) => s.questionsOpen },
  { key: 'contradicoes', label: 'Contradições', count: (s) => s.contradictions },
  { key: 'hipoteses', label: 'Hipóteses', count: (s) => s.hypotheses },
  { key: 'solucao', label: 'A Solução' },
  { key: 'iniciativas', label: 'Iniciativas', count: (s) => s.initiatives ?? 0 },
  { key: 'pecas', label: 'Peças', count: (s) => s.deliverables ?? 0 },
  { key: 'decisoes', label: 'Decisões', count: (s) => s.decisions ?? 0 },
  { key: 'efeitos', label: 'Efeitos', count: (s) => s.effects ?? 0 },
  { key: 'aprendizados', label: 'Memória', count: (s) => s.learnings ?? 0 },
  { key: 'sala', label: 'Sala do cliente' },
];
const CLIENT_STEPS = ['VER', 'O Raro™', 'Fazer', 'Medir'];

export function ProjetoPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useAuth();
  const project = useQuery({ queryKey: ['project', id], queryFn: () => api.get<ProjectDetail>(`/projects/${id}`) });
  const [params] = useSearchParams();
  const [section, setSection] = useState<Section>((((params.get('s') === 'provas' ? 'iniciativas' : params.get('s')) as Section | null) ?? 'parede'));
  const [question, setQuestion] = useState('');
  const [adding, setAdding] = useState(false);
  const [pickingCover, setPickingCover] = useState(false);

  const orgId = project.data?.organizationId;
  const internalView = project.data ? project.data.role !== 'client' : false;
  const sources = useQuery({
    queryKey: ['sources', orgId, id],
    queryFn: () => api.get<Source[]>(`/organizations/${orgId}/sources?projectId=${id}`),
    enabled: Boolean(orgId),
    refetchInterval: (q) => (q.state.data?.some((s) => sourceIsPending(s.status)) ? 2500 : false),
  });
  const pendingSuggestions = useQuery({ queryKey: ['suggestions', id, 'pending'], queryFn: () => api.get<Suggestion[]>(`/projects/${id}/suggestions?status=pending`), enabled: Boolean(orgId) && internalView });
  const summary = useQuery({ queryKey: ['summary', id], queryFn: () => api.get<ProjectSummary>(`/projects/${id}/summary`), enabled: Boolean(orgId) && internalView });
  const evidence = useQuery({
    queryKey: ['evidence', orgId, id],
    queryFn: () => api.get<Evidence[]>(`/organizations/${orgId}/evidence?projectId=${id}`),
    enabled: Boolean(orgId),
  });

  useEffect(() => {
    setQuestion(project.data?.currentQuestion ?? '');
  }, [project.data?.currentQuestion]);

  const saveQuestion = useMutation({
    mutationFn: (currentQuestion: string) => api.patch(`/projects/${id}`, { currentQuestion }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', id] }),
  });
  const saveCover = useMutation({
    mutationFn: (coverFileId: string | null) => api.patch(`/projects/${id}`, { coverFileId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['project', id] }); void qc.invalidateQueries({ queryKey: ['projects'] }); },
  });
  const newCycle = useMutation({
    mutationFn: () => api.post<Cycle>(`/projects/${id}/cycles`, { status: 'active', stage: 'observar', startsAt: new Date().toISOString() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', id] }),
  });
  const rollLive = useMutation({ mutationFn: () => api.post(`/projects/${id}/live/roll`), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['project', id] }); void qc.invalidateQueries({ queryKey: ['ver', id] }); } });
  const advance = useMutation({
    mutationFn: (cycle: Cycle) => {
      const idx = cycle.stage ? VER_STAGES.indexOf(cycle.stage) : -1;
      return api.patch(`/cycles/${cycle.id}`, { stage: VER_STAGES[Math.min(idx + 1, VER_STAGES.length - 1)] });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', id] }),
  });

  if (project.isPending) return <div className="rr-page rr-faint">carregando…</div>;
  if (project.isError || !project.data) {
    return (
      <div className="rr-page">
        <Notice tone="error">não encontramos este projeto.</Notice>
      </div>
    );
  }

  const p = project.data;
  const internal = p.role !== 'client';
  const canValidate = hasRoleAtLeast(p.role, 'partner');
  const active = p.cycles.find((c) => c.status === 'active') ?? null;
  const remaining = daysLeft(active?.endsAt);
  const countOf = (sec: (typeof SECTIONS)[number]) => (sec.key === 'sala' ? '→' : sec.key === 'machine' ? (pendingSuggestions.data?.length ?? 0) : sec.sprint ? sec.sprint : summary.data && sec.count ? sec.count(summary.data) : sec.key === 'fontes' ? (sources.data?.length ?? 0) : sec.key === 'evidencias' ? (evidence.data?.length ?? 0) : 0);
  const visibleSections = internal ? SECTIONS : SECTIONS.filter((x) => x.key === 'fontes' || x.key === 'evidencias');

  return (
    <>
      <div className="rr-topbar">
        <Breadcrumbs items={[{ label: 'Projetos', render: (l) => <Link to="/projetos">{l}</Link> }, { label: p.organization.name }, { label: p.name }]} />
        <div className="rr-row">
          <Tag tone="outline">{PROJECT_TYPE_LABELS[p.type]}</Tag>
          {p.brand && <BrandSwitch projectId={p.id} organizationId={p.organizationId} brandId={p.brandId ?? null} name={p.brand.name} canEdit={internal} />}
          {p.scope && <Link to={`/clientes/${p.organizationId}?aba=praças`} title="VER localizado: este projeto olha um lugar, não o cliente inteiro"><Tag tone="accent">localizado · {p.scope.label}</Tag></Link>}
          <span className="rr-label">{PROJECT_STATUS_LABELS[p.status]}</span>
          <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
            + colocar na Machine
          </Button>
        </div>
      </div>

      <div className="rr-page rr-page--tight rr-stack rr-stack--7">
        <header className="rr-stack rr-stack--5">
          <div className="rr-row rr-row--between rr-row--wrap">
            <h1 className="rr-title">
              <span style={{ fontWeight: 600 }}>RARO</span>
              <span className="rr-faint"> × </span>
              <span style={{ fontWeight: 600 }}>{p.organization.name.toUpperCase()}</span>
            </h1>
            <span className="rr-heading rr-muted">{p.name}</span>
          </div>

          <div className="rr-hero rr-hero--photo">
            {p.cover && <img className="rr-hero__img" src={p.cover.url} alt="" aria-hidden="true" />}
            {internal && (
              <div className="rr-hero__actions">
                <Button size="sm" variant="quiet" onClick={() => setPickingCover(true)}>{p.cover ? 'trocar capa' : 'escolher capa'}</Button>
              </div>
            )}
            <Label>Questão atual</Label>
            {internal ? (
              <textarea
                className="rr-inline-edit rr-hero__q"
                rows={3}
                value={question}
                placeholder="Como podemos … ?"
                onChange={(e) => setQuestion(e.target.value)}
                onBlur={() => {
                  if (question.trim() !== (p.currentQuestion ?? '')) saveQuestion.mutate(question.trim());
                }}
                style={{ resize: 'none', background: 'transparent', width: '100%', maxWidth: 'none', overflow: 'hidden' }}
              />
            ) : (
              <p className="rr-hero__q">{p.currentQuestion ?? <span className="rr-faint">—</span>}</p>
            )}
          </div>

          <ContextBase projectId={p.id} compact />
          <div className="rr-row rr-row--between rr-row--wrap">
            <Stepper
              steps={CLIENT_STEPS}
              hints={['sessões, fontes, evidências: o que existe', 'hipóteses e a Solução: o que só este cliente pode fazer', 'iniciativas e peças: o que vira prática', 'efeitos e aprendizados: o que mudou']}
              current={(summary.data?.effects ?? 0) > 0 ? 3 : (summary.data?.initiatives ?? 0) + (summary.data?.deliverables ?? 0) > 0 ? 2 : (summary.data?.hypotheses ?? 0) > 0 ? 1 : 0}
              onStep={(i) => (i === 0 ? navigate(`/projetos/${p.id}/ver`) : setSection((['sessoes', 'hipoteses', 'pecas', 'efeitos'] as Section[])[i]!))}
            />
            {active ? (
              <div className="rr-row" style={{ gap: 'var(--rr-space-4)' }}>
                <Link to={`/projetos/${p.id}/ver`} className="rr-label rr-label--strong" style={{ textDecoration: 'underline', textUnderlineOffset: 4, textDecorationColor: 'var(--rr-border-strong)' }}>
                  {active.name} · {stageLabel(active.stage)} →
                </Link>
                {p.type === 'live' ? (
                  <>
                    <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>LIVE {p.cadence ? LIVE_CADENCE_LABELS[p.cadence] : ''}{p.nextCycleAt ? ` · próximo período em ${Math.max(0, daysLeft(p.nextCycleAt) ?? 0)} dia(s)` : ''}</span>
                    {canValidate && <Button size="sm" variant="quiet" onClick={() => rollLive.mutate()} disabled={rollLive.isPending} title="encerra o período, gera a síntese e abre o próximo">{rollLive.isPending ? 'virando…' : 'virar período agora'}</Button>}
                  </>
                ) : (
                  remaining !== null && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{remaining} dias restantes</span>
                )}
                {internal && (
                  <Button size="sm" onClick={() => advance.mutate(active)} disabled={advance.isPending || active.stage === 'provar'}>
                    avançar etapa →
                  </Button>
                )}
              </div>
            ) : (
              internal && (
                <Button size="sm" variant="primary" onClick={() => newCycle.mutate()} disabled={newCycle.isPending}>
                  iniciar ciclo
                </Button>
              )
            )}
          </div>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', gap: 'var(--rr-space-6)', alignItems: 'start' }}>
          <Index>
            {visibleSections.map((s) => (
              <IndexItem key={s.key} label={s.label} count={countOf(s)} current={section === s.key} onClick={() => setSection(s.key)} />
            ))}
          </Index>

          <div className="rr-stack rr-stack--5" style={{ minWidth: 0 }}>
            {SECTION_GLYPH[section] && <Glyph name={SECTION_GLYPH[section]!} size={56} className="rr-section-glyph" />}
            {section === 'fontes' && (
              <section className="rr-section">
                <div className="rr-section__head">
                  <Label>Fontes</Label>
                  <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
                    {sources.data?.length ?? 0} itens
                  </span>
                </div>
                {sources.isSuccess && sources.data.length === 0 ? (
                  <Card style={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
                    <div className="rr-stack rr-stack--3" style={{ textAlign: 'center', maxWidth: 420 }}>
                      <p className="rr-heading">
                        <Serif italic>a Machine ainda não viu nada.</Serif>
                      </p>
                      <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>
                        entrevistas, fotos, planilhas, apresentações, links, áudios. coloque o material bruto e a Machine extrai, fragmenta e indexa.
                      </p>
                      <div>
                        <Button variant="primary" onClick={() => setAdding(true)}>
                          + colocar na Machine
                        </Button>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <SourceList sources={sources.data ?? []} />
                )}
              </section>
            )}

            {section === 'evidencias' && (
              <section className="rr-section">
                <div className="rr-section__head">
                  <Label>Evidências</Label>
                  <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
                    {evidence.data?.filter((e) => e.humanStatus === 'accepted').length ?? 0} aceitas · {evidence.data?.filter((e) => e.humanStatus === 'under_review').length ?? 0} em revisão
                  </span>
                </div>
                {evidence.isSuccess && evidence.data.length === 0 ? (
                  <EmptyState title="nenhuma evidência marcada.">abra uma fonte e marque os trechos que importam.</EmptyState>
                ) : (
                  <div className="rr-stack rr-stack--3">
                    {evidence.data?.map((e) => <EvidenceCard key={e.id} evidence={e} canValidate={canValidate} showSource />)}
                  </div>
                )}
              </section>
            )}

            {section === 'machine' && <MachineSection projectId={p.id} />}
            {section === 'sala' && <ClientRoomCuration projectId={p.id} orgSlug={p.organization.slug} role={p.role} organizationId={p.organizationId} />}
            {section === 'parede' && <WallCanvas projectId={p.id} canEdit={internal} />}
            {section === 'observacoes' && <ObservationsSection projectId={p.id} canEdit={internal} />}
            {section === 'sinais' && <SignalsSection projectId={p.id} canEdit={internal} />}
            {section === 'perguntas' && <QuestionsSection projectId={p.id} canEdit={internal} />}
            {section === 'contradicoes' && <ContradictionsSection projectId={p.id} />}
            {section === 'hipoteses' && <HypothesesSection projectId={p.id} canEdit={internal} />}
            {section === 'sessoes' && <SessionsSection projectId={p.id} organizationId={p.organizationId} canEdit={internal} />}
            {section === 'entrevistas' && internal && <InterviewProfiles organizationId={p.organizationId} projectId={p.id} />}
            {section === 'solucao' && <SolutionSection projectId={p.id} canEdit={internal} canApprove={canValidate} />}
            {section === 'iniciativas' && <InitiativesSection projectId={p.id} canEdit={internal} />}
            {section === 'pecas' && <PecasBoard projectId={p.id} organizationId={p.organizationId} canEdit={internal} />}
            {section === 'decisoes' && <DecisionsSection projectId={p.id} canEdit={internal} canDecide={canValidate} />}
            {section === 'efeitos' && <EffectsSection projectId={p.id} canEdit={internal} />}
            {section === 'aprendizados' && <LearningsSection projectId={p.id} canEdit={internal} />}
          </div>
        </div>

        <AskBar
          placeholder="Pergunte à Machine..."
          hint="a Machine responde na MEMÓRIA, com citações deste projeto."
          onSubmit={(q) => navigate(`/memoria?q=${encodeURIComponent(q)}&org=${p.organizationId}&project=${p.id}`)}
        />
      </div>

      <AddSourceDrawer open={adding} onClose={() => setAdding(false)} organizationId={p.organizationId} projectId={p.id} />
      <ImagePicker open={pickingCover} onClose={() => setPickingCover(false)} organizationId={p.organizationId} projectId={p.id} current={p.coverFileId ?? null} onPick={(fid) => saveCover.mutate(fid)} eyebrow="Capa do projeto" title="a foto que abre este projeto" />
      {me === null && null}
    </>
  );
}

/** A marca do projeto (só aparece em cliente com mais de uma). Trocar leva junto as fontes e as conversas do projeto. */
function BrandSwitch({ projectId, organizationId, brandId, name, canEdit }: { projectId: string; organizationId: string; brandId: string | null; name: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { brands } = useBrands(organizationId);
  const move = useMutation({ mutationFn: (value: string) => api.patch(`/projects/${projectId}`, { brandId: value === 'grupo' ? null : value }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['project'] }); void qc.invalidateQueries({ queryKey: ['projects'] }); void qc.invalidateQueries({ queryKey: ['brands', organizationId] }); } });
  if (!canEdit) return <Tag tone="outline">{name}</Tag>;
  return (
    <Select value={brandId ?? 'grupo'} onChange={(e) => move.mutate(e.target.value)} disabled={move.isPending} aria-label="marca do projeto" title="marca do projeto: ele só enxerga o material desta marca e o que é do grupo" style={{ width: 'auto' }}>
      {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      <option value="grupo">grupo (sem marca)</option>
    </Select>
  );
}
